import React, { useState } from "react";
import { Link } from "react-router-dom";
import Icon from "../components/Icon";
import { useData } from "../data/DataProvider";
import { roleColor } from "../data/roleColors";
import { isBenchEligible, personAssignments } from "../services/utilization";
import { scoreTeam, teamVerdict } from "../services/compatibility";
import { getArchetype } from "../data/archetypes";
import GanttPlanner from "./GanttPlanner";
import ScenarioPlanner from "./ScenarioPlanner";
import "../styles.css";

function getInitials(name) { return name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase(); }

// Field/deployable roles count toward the bench & workload. Office/support
// staff (precon, accountants, directors, admin, etc.) are intentionally excluded.
// Bench/availability uses the shared rule: PM family + superintendents +
// PE/FE + QA/QC. Excludes general supts, carpenters, office & leadership.
function isFieldRole(role) {
  return isBenchEligible({ CompanyRole: role });
}
// Roles that legitimately span multiple jobs at once (don't flag as over-allocated).
const SHARED_KEYWORDS = ["qa", "qc", "quality", "px", "project executive"];
const isSharedAssignment = (role) => SHARED_KEYWORDS.some(k => (role || "").toLowerCase().includes(k));
const allocOf = (m) => (m.AllocationPercent != null && m.AllocationPercent !== "" ? Number(m.AllocationPercent) : 100);

function projectStatus(profiledPeople, memberCount) {
  if (memberCount === 0) return "empty";
  if (profiledPeople.length === 0) return "unanalyzed";
  const ts = scoreTeam(profiledPeople);
  if (!ts) return "unanalyzed";
  if (ts.score >= 75) return "strong";
  if (ts.score >= 55) return "moderate";
  return "watch";
}
const STATUS_META = {
  strong: { color: "var(--green-bar)" }, moderate: { color: "var(--gold)" },
  watch: { color: "var(--red-bar)" }, unanalyzed: { color: "var(--text-muted)" }, empty: { color: "var(--text-muted)" },
};

function ProjectRow({ project, members, allPeople }) {
  const [expanded, setExpanded] = useState(false);
  const enriched = members.map(m => ({ ...m, person: allPeople.find(p => p.FullName === m.PersonName) || null }));
  const profiledPeople = enriched.filter(e => e.person).map(e => e.person);
  const ts = profiledPeople.length >= 2 ? scoreTeam(profiledPeople) : null;
  const status = projectStatus(profiledPeople, members.length);
  const dueDate = project.CompletionDate ? new Date(project.CompletionDate).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : null;

  return (
    <div className="wp-project-row">
      <div className="wp-project-main" onClick={() => setExpanded(!expanded)}>
        <div className="wp-status-bar" style={{ background: STATUS_META[status].color }} />
        <div className="wp-project-info">
          <div className="wp-project-num">{project.Title}</div>
          <div className="wp-project-name">{project.JobName}</div>
          <div className="wp-project-meta">
            <span>PX: {project.ProjectExecutive}</span>
            {dueDate && <span>Due {dueDate}</span>}
          </div>
        </div>
        <div className="wp-avatars">
          {enriched.slice(0, 7).map((m, i) => (
            <div key={i} className="avatar" style={{ width: 28, height: 28, fontSize: 9, background: roleColor(m.ProjectRole), opacity: m.person ? 1 : 0.35 }}
              title={`${m.PersonName} · ${m.ProjectRole}${!m.person ? " · No CI Profile" : ""}`}>
              {getInitials(m.PersonName)}
            </div>
          ))}
          {enriched.length > 7 && <div className="avatar other" style={{ width: 28, height: 28, fontSize: 9 }}>+{enriched.length - 7}</div>}
        </div>
        <div className="wp-score-col">
          {ts ? (
            <>
              <div className="wp-score-num" style={{ color: ts.score >= 75 ? "var(--green)" : ts.score >= 55 ? "var(--gold-text)" : "var(--red)" }}>{ts.score}</div>
              <div className="wp-score-label">{teamVerdict(ts.score)}</div>
            </>
          ) : (
            <div className="wp-score-label" style={{ color: "var(--text-muted)" }}>{profiledPeople.length}/{members.length} profiled</div>
          )}
        </div>
        <div style={{ color: "var(--text-muted)", fontSize: 14, padding: "0 8px", transform: expanded ? "rotate(180deg)" : "none" }}>▾</div>
      </div>

      {expanded && (
        <div className="wp-expanded">
          {enriched.map((m, i) => {
            const arch = m.person ? getArchetype(m.person.Archetype) : null;
            return (
              <div key={i} className="wp-member-row">
                <div className="avatar" style={{ width: 30, height: 30, fontSize: 10, background: roleColor(m.ProjectRole), opacity: m.person ? 1 : 0.4 }}>{getInitials(m.PersonName)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: m.person ? "var(--text-primary)" : "var(--text-muted)" }}>{m.PersonName}</div>
                  {m.person && arch ? (
                    <div style={{ fontSize: 11, color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 4 }}><Icon name={arch.icon} size={11} /> {m.person.Archetype}</div>
                  ) : (
                    <div style={{ fontSize: 11, color: "var(--text-muted)", fontStyle: "italic" }}>No CI profile</div>
                  )}
                </div>
                <div style={{ fontSize: 11, color: roleColor(m.ProjectRole), fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>{m.ProjectRole} · {allocOf(m)}%</div>
              </div>
            );
          })}
          <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
            <Link to={`/projects/${encodeURIComponent(project.Title)}`} className="btn btn-secondary" style={{ fontSize: 12, padding: "6px 14px" }}>Full Analysis</Link>
            <Link to="/admin/import" className="btn btn-ghost" style={{ fontSize: 12, padding: "6px 14px" }}>Edit Team</Link>
          </div>
        </div>
      )}
    </div>
  );
}

// Each field person with their CURRENT utilization (time-aware: ended
// assignments have rolled off; back-to-back jobs don't stack to a false 200%).
function WorkloadCard({ person, util, peak, assignments, shared }) {
  const arch = getArchetype(person.Archetype);
  const over = peak > 100;
  const barColor = util === 0 ? "var(--blue)" : shared ? "var(--blue)" : over ? "var(--red)" : util >= 100 ? "var(--green)" : "var(--gold)";
  const tag = util === 0 ? "Available"
    : shared ? `Shared · ${assignments.length} job${assignments.length === 1 ? "" : "s"}`
    : over ? "Over-allocated (overlap)" : util >= 100 ? "Full" : `${100 - util}% open`;
  return (
    <Link to={`/people/${person.Id}`} className="wp-bench-card" style={{ flexDirection: "column", alignItems: "stretch", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div className="avatar" style={{ width: 32, height: 32, fontSize: 11, flexShrink: 0, background: roleColor(person.CompanyRole) }}>{getInitials(person.FullName)}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{person.FullName}</div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "1px" }}>{person.CompanyRole}</div>
        </div>
        <span className="person-archetype" style={{ margin: 0, fontSize: 10, padding: "2px 7px", display: "inline-flex", alignItems: "center", gap: 4 }}>
          <Icon name={arch.icon} size={10} /> {person.Archetype}
        </span>
      </div>
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 3 }}>
          <span style={{ color: "var(--text-secondary)" }}>{assignments.map(a => a.ProjectJobNumber).join(", ") || "Unassigned"}</span>
          <span style={{ fontWeight: 800, color: barColor }}>{util}% · {tag}</span>
        </div>
        <div style={{ height: 6, background: "var(--bg-elevated)", borderRadius: 3, overflow: "hidden" }}>
          <div style={{ width: `${Math.min(util, 100)}%`, height: "100%", background: barColor, borderRadius: 3 }} />
        </div>
      </div>
    </Link>
  );
}

export default function WorkforcePlanner() {
  const { people, projects, teamMembers, loading, error } = useData();
  const [pxFilter, setPxFilter] = useState("All");
  const [view, setView] = useState("timeline");

  if (loading) return <div className="page"><div className="empty"><div className="spinner" /><div className="empty-text" style={{ marginTop: 16 }}>Loading planner…</div></div></div>;
  if (error) return <div className="page"><div className="empty"><div className="empty-text">Couldn't load data: {error}</div></div></div>;

  const allPeople = people.filter(p => p.IsActive !== false);
  const activeProjects = projects.filter(p => (p.Status || "Active") === "Active");

  // Workload & availability — time-aware, same engine as the Assignments page:
  // ended assignments drop off, and someone rolling from one job onto the next
  // is NOT double-counted (peak concurrent load, not a lifetime sum).
  const fieldPeople = allPeople.filter(p => isFieldRole(p.CompanyRole))
    .map(p => {
      const u = personAssignments(p, teamMembers, projects);
      const assignments = u.rows.map(r => r.member);
      return {
        person: p,
        util: u.currentUtil,
        peak: u.peakUtil,
        assignments,
        shared: assignments.some(a => isSharedAssignment(a.ProjectRole)),
      };
    })
    .sort((a, b) => a.util - b.util);
  const benchCount = fieldPeople.filter(f => f.assignments.length === 0).length;

  const pxList = ["All", ...[...new Set(activeProjects.map(p => p.ProjectExecutive).filter(Boolean))].sort((a, b) => a.localeCompare(b))];
  const visible = pxFilter === "All" ? activeProjects : activeProjects.filter(p => p.ProjectExecutive === pxFilter);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Workforce Planner</div>
          <div className="page-subtitle">Deployment, workload &amp; pipeline · Culture Index intelligence</div>
        </div>
        <div className="stat-row">
          <div className="stat-card"><div className="stat-bar" style={{ background: "var(--gold)" }} /><div className="stat-num">{activeProjects.length}</div><div className="stat-label">Active Jobs</div></div>
          <div className="stat-card"><div className="stat-bar" style={{ background: "var(--blue)" }} /><div className="stat-num">{benchCount}</div><div className="stat-label">On Bench</div></div>
        </div>
      </div>

      <div className="wp-view-toggle">
        <button className={`wp-view-btn ${view === "timeline" ? "active" : ""}`} onClick={() => setView("timeline")}><Icon name="calendar" size={15} /> Timeline &amp; Pipeline</button>
        <button className={`wp-view-btn ${view === "scenarios" ? "active" : ""}`} onClick={() => setView("scenarios")}><Icon name="bolt" size={15} /> Scenarios</button>
        <button className={`wp-view-btn ${view === "deployment" ? "active" : ""}`} onClick={() => setView("deployment")}><Icon name="users" size={15} /> Deployment &amp; Workload</button>
        <Link to="/financials" className="wp-view-btn" style={{ textDecoration: "none" }} title="GC Burn-Down moved to Financials"><Icon name="chart" size={15} /> GC Burn-Down ↗</Link>
      </div>

      {view === "timeline" ? (
        <GanttPlanner />
      ) : view === "scenarios" ? (
        <ScenarioPlanner />
      ) : (
        <div className="wp-layout">
          <div className="wp-main">
            <div className="section-header">
              <div className="section-title">Project Deployment</div>
              <div className="filter-row">
                {pxList.map(px => (
                  <div key={px} className={`filter-chip ${pxFilter === px ? "active" : ""}`} onClick={() => setPxFilter(px)}>{px === "All" ? "All PX" : px}</div>
                ))}
              </div>
            </div>
            {visible.map(project => (
              <ProjectRow key={project.Title} project={project} members={teamMembers.filter(m => m.ProjectJobNumber === project.Title)} allPeople={allPeople} />
            ))}
          </div>

          <div className="wp-sidebar">
            <div className="section-title mb-16">Workload &amp; Availability</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 12 }}>Field staff, least-utilized first. Load is as of today — people who rolled off are back on the bench.</div>
            {fieldPeople.length === 0 ? (
              <div className="empty" style={{ padding: "32px 16px" }}><div className="empty-text">No field staff found.</div></div>
            ) : (
              fieldPeople.map(f => <WorkloadCard key={f.person.Id} person={f.person} util={f.util} peak={f.peak} assignments={f.assignments} shared={f.shared} />)
            )}
          </div>
        </div>
      )}
    </div>
  );
}